import { useResizableSeparator } from '@tunasong/ui-lib'
import type { FC } from 'react'
import styles from './resize-handle.module.css'

interface ResizeHandleProps {
  /** Current sidebar width as a percentage of the row (0-100). */
  sidebarWidth: number
  /** Persist the new width — typically writes to a Zustand store with localStorage. */
  setSidebarWidth: (width: number) => void
  /** Bounds the user can resize within. Default 15..70. */
  min?: number
  max?: number
}

/** The notes panel's width before any resize: Perform's store default */
const defaultWidth = 30
/** Percent per arrow key press */
const keyStep = 5

/** px of pointer movement as percent of the row the panel shares */
const percentOfRow = (handle: HTMLElement) => {
  const rowWidth = handle.parentElement?.getBoundingClientRect().width ?? 0
  return rowWidth > 0 ? 100 / rowWidth : 0
}

/**
 * Thin draggable bar between the content pane and the sidebar in performance mode.
 * Updates `sidebarWidth` (a percentage of the parent row's width) on pointer drag.
 *
 * Why this and not a third-party splitter: keeping `content` under a stable parent
 * across sidebar toggles requires that the splitter component never unmount. Most
 * splitter libraries lose their internal sizing state when `right={null}`. A small
 * custom handle lets us stay in a stable React tree and still resize.
 *
 * It behaves as the app's other separators (GH #2976): ← widens and → narrows by 5%, Home and End go to the bounds,
 * a click with no drag switches between the default and the max, and it says its width in percent.
 */
export const ResizeHandle: FC<ResizeHandleProps> = ({ sidebarWidth, setSidebarWidth, min = 15, max = 70 }) => {
  const separatorProps = useResizableSeparator({
    value: sidebarWidth,
    min,
    max,
    defaultValue: defaultWidth,
    label: 'Resize notes panel',
    onResize: setSidebarWidth,
    onCommit: setSidebarWidth,
    step: keyStep,
    unitsPerPx: percentOfRow,
    decimals: 1,
    valueText: `Notes panel, ${Math.round(sidebarWidth)}% of the width`,
  })

  return <div {...separatorProps} className={styles.handle} />
}

export default ResizeHandle
